import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox, Select, Spin, Tag, message } from 'antd';
import {
  LeftOutlined, EnvironmentOutlined, AppstoreOutlined,
  LaptopOutlined, PrinterOutlined, ToolOutlined, CoffeeOutlined,
  CarOutlined, ExperimentOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { getStoredUser, isSuperAdmin } from '../userAuth';
import { H5_TAB_BAR_PX } from '../h5Layout';

const TABS = [
  { key: 'eligible', label: '可处置', status: null },
  { key: 'unreported', label: '未上报', status: 0 },
  { key: 'reported', label: '已上报', status: 1 },
  { key: 'pending', label: '未完成', status: 2 },
  { key: 'done', label: '已完成', status: 3 },
];

const TYPE_STYLES = {
  '电子设备': { color: '#1677FF', bg: '#E8F0FE', icon: <LaptopOutlined /> },
  '办公设备': { color: '#52c41a', bg: '#E8F8E0', icon: <PrinterOutlined /> },
  '办公家具': { color: '#fa8c16', bg: '#FFF3E0', icon: <CoffeeOutlined /> },
  '交通工具': { color: '#722ed1', bg: '#F3E8FF', icon: <CarOutlined /> },
  '实验设备': { color: '#eb2f96', bg: '#FFE8F0', icon: <ExperimentOutlined /> },
  '维修工具': { color: '#13c2c2', bg: '#E0F8F8', icon: <ToolOutlined /> },
};
const DEFAULT_TYPE = { color: '#8c8c8c', bg: '#f0f0f0', icon: <AppstoreOutlined /> };

function getTypeStyle(type) {
  return TYPE_STYLES[type] || DEFAULT_TYPE;
}

function formatAssetCodeDisplay(code, maxLen = 12, tailLen = 8) {
  const s = String(code || '').trim();
  if (!s) return '-';
  if (s.length <= maxLen) return s;
  return `…${s.slice(-tailLen)}`;
}

const SWIPE_W = 88;

export default function Disposal() {
  const navigate = useNavigate();
  const user = getStoredUser();
  const canUse = isSuperAdmin(user);
  const [tab, setTab] = useState('eligible');
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [months, setMonths] = useState([]);
  const [batchMonth, setBatchMonth] = useState('');
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [openSwipeId, setOpenSwipeId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const movedRef = useRef(false);
  const tabScrollRef = useRef(null);

  const tabMeta = useMemo(() => TABS.find((t) => t.key === tab) || TABS[0], [tab]);
  const needMonth = tabMeta.status === 1 || tabMeta.status === 2 || tabMeta.status === 3;

  const loadMonths = useCallback(async () => {
    if (!needMonth) {
      setMonths([]);
      return;
    }
    try {
      const res = await api.get('/disposal/months', { params: { status: tabMeta.status } });
      const list = res?.items || [];
      setMonths(list);
      setBatchMonth((prev) => {
        if (list.length && (!prev || !list.includes(prev))) return list[0];
        if (!list.length) return '';
        return prev;
      });
    } catch {
      setMonths([]);
    }
  }, [needMonth, tabMeta.status]);

  const load = useCallback(async () => {
    setLoading(true);
    setSelected([]);
    setOpenSwipeId(null);
    try {
      if (tab === 'eligible') {
        const res = await api.get('/disposal/eligible', { params: { page: 1, page_size: 100 } });
        setItems(res?.items || []);
      } else {
        const params = { status: tabMeta.status, page: 1, page_size: 100 };
        if (needMonth && batchMonth) params.batch_month = batchMonth;
        const res = await api.get('/disposal/list', { params });
        setItems(res?.items || []);
      }
    } catch (err) {
      message.error(err.message || '加载失败');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [tab, tabMeta.status, needMonth, batchMonth]);

  useEffect(() => {
    if (!canUse) return;
    loadMonths();
  }, [canUse, loadMonths]);

  useEffect(() => {
    if (!canUse) return;
    if (needMonth && months.length && !batchMonth) return;
    load();
  }, [canUse, load, needMonth, months.length, batchMonth]);

  // 切换 tab 时把当前标签滚到可视区，避免左侧被裁切
  useEffect(() => {
    const root = tabScrollRef.current;
    if (!root) return;
    const el = root.querySelector(`[data-tab="${tab}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    }
  }, [tab]);

  const rowKey = (row) => (tab === 'eligible' ? (row.asset_id || row.id) : row.id);
  const assetIdOf = (row) => row.asset_id || row.id;

  const toggle = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleAll = () => {
    if (selected.length === items.length) setSelected([]);
    else setSelected(items.map((x) => rowKey(x)));
  };

  const runAction = async (kind = 'default') => {
    if (!selected.length) {
      message.warning('请先选择资产');
      return;
    }
    setSubmitting(true);
    try {
      if (tab === 'eligible') {
        await api.post('/disposal/submit', { asset_ids: selected, remark: '超管提交未上报' });
        message.success('已提交至未上报');
      } else if (tab === 'unreported') {
        await api.post('/disposal/report', { disposal_ids: selected });
        message.success('已标记为已上报');
      } else if (tab === 'reported') {
        if (kind === 'reject') {
          await api.post('/disposal/reject', { disposal_ids: selected, remark: '已上报审核未通过' });
          message.success('已退回未上报');
        } else {
          await api.post('/disposal/audit', { disposal_ids: selected });
          message.success('已转入已处置未完成');
        }
      } else if (tab === 'pending') {
        await api.post('/disposal/complete', { disposal_ids: selected });
        message.success('已标记为已完成，资产已从列表移除');
      }
      load();
      loadMonths();
    } catch (err) {
      message.error(err.message || '操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  const actionLabel = {
    eligible: '提交至未上报',
    unreported: '标记已上报',
    reported: '',
    pending: '标记已完成',
    done: '',
  }[tab];

  const handleTouchStart = (e, id) => {
    touchStartXRef.current = e.touches?.[0]?.clientX || 0;
    touchStartYRef.current = e.touches?.[0]?.clientY || 0;
    movedRef.current = false;
    setDraggingId(id);
    setDragOffset(openSwipeId === id ? -SWIPE_W : 0);
  };

  const handleTouchMove = (e, id) => {
    if (draggingId !== id) return;
    const x = e.touches?.[0]?.clientX || 0;
    const y = e.touches?.[0]?.clientY || 0;
    const dx = x - touchStartXRef.current;
    const dy = y - touchStartYRef.current;
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) movedRef.current = true;
    if (Math.abs(dy) > Math.abs(dx)) return;
    const base = openSwipeId === id ? -SWIPE_W : 0;
    setDragOffset(Math.max(-SWIPE_W, Math.min(0, base + dx)));
  };

  const handleTouchEnd = (id) => {
    if (draggingId !== id) return;
    const shouldOpen = dragOffset < -SWIPE_W / 2;
    setOpenSwipeId(shouldOpen ? id : null);
    setDragOffset(0);
    setDraggingId(null);
  };

  if (!canUse) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f7fa', padding: 24, textAlign: 'center' }}>
        <div style={{ marginTop: 80, color: '#999' }}>仅超级管理员可管理资产处置</div>
        <Button style={{ marginTop: 16 }} onClick={() => navigate(-1)}>返回</Button>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f7fa',
      paddingBottom: actionLabel ? H5_TAB_BAR_PX + 70 : H5_TAB_BAR_PX + 16,
    }}
    >
      {/* 固定顶栏：标题 + 完整标签，避免左侧裁切 */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 30, background: '#fff',
        borderBottom: '1px solid #f0f0f0',
      }}
      >
        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px' }}>
          <LeftOutlined onClick={() => navigate(-1)} style={{ fontSize: 18, marginRight: 12 }} />
          <span style={{ fontSize: 17, fontWeight: 600, flex: 1 }}>资产处置</span>
        </div>
        <div
          ref={tabScrollRef}
          style={{
            display: 'flex',
            gap: 6,
            padding: '0 12px 10px',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
          }}
        >
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <div
                key={t.key}
                data-tab={t.key}
                onClick={() => { setTab(t.key); setBatchMonth(''); setSelected([]); setOpenSwipeId(null); }}
                style={{
                  flex: '0 0 auto',
                  padding: '6px 14px',
                  borderRadius: 16,
                  fontSize: 13,
                  fontWeight: active ? 600 : 400,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  background: active ? '#fa8c16' : '#f5f7fa',
                  color: active ? '#fff' : '#666',
                }}
              >
                {t.label}
              </div>
            );
          })}
        </div>
        {needMonth ? (
          <div style={{ padding: '0 12px 10px' }}>
            <Select
              style={{ width: '100%' }}
              placeholder="按月份批次查看"
              value={batchMonth || undefined}
              onChange={setBatchMonth}
              options={months.map((m) => ({
                value: m,
                label: dayjs(`${m}-01`).format('YYYY年M月'),
              }))}
              allowClear={!months.length}
            />
          </div>
        ) : null}
      </div>

      <div style={{ padding: 12 }}>
        {items.length > 0 && actionLabel ? (
          <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Checkbox
              checked={selected.length > 0 && selected.length === items.length}
              indeterminate={selected.length > 0 && selected.length < items.length}
              onChange={toggleAll}
            >
              全选
            </Checkbox>
            <span style={{ fontSize: 12, color: '#999' }}>已选 {selected.length}</span>
          </div>
        ) : null}

        <Spin spinning={loading}>
          {items.length === 0 && !loading ? (
            <div style={{ textAlign: 'center', color: '#999', padding: 60 }}>暂无数据</div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 10,
            }}
            >
              {items.map((row) => {
                const id = rowKey(row);
                const aid = assetIdOf(row);
                const checked = selected.includes(id);
                const reported = Number(row.status) === 1;
                const ts = getTypeStyle(row.asset_type);
                const offset = draggingId === id
                  ? dragOffset
                  : (openSwipeId === id ? -SWIPE_W : 0);
                return (
                  <div
                    key={id}
                    style={{
                      position: 'relative',
                      borderRadius: 10,
                      overflow: 'hidden',
                      alignSelf: 'start',
                    }}
                  >
                    <div style={{
                      position: 'absolute', right: 0, top: 0, bottom: 0, width: SWIPE_W,
                      background: '#1677ff', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 500,
                    }}
                      onClick={() => {
                        setOpenSwipeId(null);
                        navigate(`/assets/${aid}?from=disposal`);
                      }}
                    >
                      查看详情
                    </div>
                    <div
                      onTouchStart={(e) => handleTouchStart(e, id)}
                      onTouchMove={(e) => handleTouchMove(e, id)}
                      onTouchEnd={() => handleTouchEnd(id)}
                      onClick={() => {
                        if (movedRef.current || openSwipeId === id) {
                          if (openSwipeId === id) setOpenSwipeId(null);
                          return;
                        }
                        if (actionLabel) toggle(id);
                      }}
                      style={{
                        position: 'relative',
                        zIndex: 1,
                        background: reported ? '#fff7e6' : '#fff',
                        borderRadius: 10,
                        overflow: 'hidden',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                        border: checked
                          ? '2px solid #1677ff'
                          : (reported ? '2px solid #ffd591' : '2px solid transparent'),
                        transform: `translateX(${offset}px)`,
                        transition: draggingId === id ? 'none' : 'transform 0.2s ease',
                        touchAction: 'pan-y',
                        cursor: actionLabel ? 'pointer' : 'default',
                      }}
                    >
                      {actionLabel ? (
                        <div style={{
                          position: 'absolute', right: 8, top: 8, zIndex: 3,
                          width: 18, height: 18, borderRadius: 9,
                          border: `1px solid ${checked ? '#1677ff' : '#d9d9d9'}`,
                          background: checked ? '#1677ff' : '#fff',
                          color: '#fff', fontSize: 12,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        >
                          {checked ? '✓' : ''}
                        </div>
                      ) : null}
                      <div style={{
                        background: ts.bg, height: 80,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                      >
                        {row.cover_photo_url ? (
                          <img
                            src={row.cover_photo_url}
                            alt={row.asset_name || '资产'}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <span style={{ fontSize: 32, color: ts.color }}>{ts.icon}</span>
                        )}
                      </div>
                      <div style={{ padding: '10px 12px' }}>
                        <div style={{
                          fontSize: 14, fontWeight: 600, color: reported ? '#d46b08' : '#222',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          paddingRight: actionLabel ? 18 : 0,
                        }}
                        >
                          {row.asset_name}
                        </div>
                        <div style={{ marginTop: 4 }}>
                          <Tag
                            color={reported ? 'orange' : 'default'}
                            style={{ margin: 0, fontSize: 11, lineHeight: '18px', padding: '0 6px' }}
                          >
                            {row.status_text || '可处置'}
                          </Tag>
                        </div>
                        {reported ? (
                          <div style={{ fontSize: 11, color: '#d46b08', marginTop: 4, fontWeight: 600 }}>
                            该资产已进行处置申请！
                          </div>
                        ) : null}
                        <div
                          title={row.asset_code || ''}
                          style={{
                            fontSize: 12, color: '#666', marginTop: 4,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                        >
                          {formatAssetCodeDisplay(row.asset_code)}
                        </div>
                        <div style={{
                          fontSize: 12, color: '#999', marginTop: 2,
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}
                        >
                          <span style={{ fontSize: 11 }}>👤</span>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.user_name || '-'}
                          </span>
                        </div>
                        <div style={{
                          fontSize: 12, color: '#999', marginTop: 2,
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}
                        >
                          <EnvironmentOutlined style={{ fontSize: 11 }} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.location_name || '-'}
                          </span>
                        </div>
                        {row.batch_month ? (
                          <div style={{ fontSize: 11, color: '#bbb', marginTop: 2 }}>批次 {row.batch_month}</div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Spin>
      </div>

      {tab === 'reported' ? (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: H5_TAB_BAR_PX,
          padding: '10px 16px', background: '#fff', borderTop: '1px solid #f0f0f0', zIndex: 20,
          display: 'flex', gap: 10,
        }}
        >
          <Button
            block
            size="large"
            loading={submitting}
            disabled={!selected.length}
            onClick={() => runAction('reject')}
            style={{ borderRadius: 8, flex: 1 }}
          >
            未通过·退回未上报（{selected.length}）
          </Button>
          <Button
            type="primary"
            block
            size="large"
            loading={submitting}
            disabled={!selected.length}
            onClick={() => runAction('approve')}
            style={{ borderRadius: 8, background: '#52c41a', borderColor: '#52c41a', flex: 1 }}
          >
            通过→未完成（{selected.length}）
          </Button>
        </div>
      ) : actionLabel ? (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: H5_TAB_BAR_PX,
          padding: '10px 16px', background: '#fff', borderTop: '1px solid #f0f0f0', zIndex: 20,
        }}
        >
          <Button
            type="primary"
            block
            size="large"
            loading={submitting}
            disabled={!selected.length}
            onClick={() => runAction()}
            style={{ borderRadius: 8, background: '#fa8c16', borderColor: '#fa8c16' }}
          >
            {actionLabel}（{selected.length}）
          </Button>
        </div>
      ) : null}
    </div>
  );
}
